import { useEffect, useState, useRef, useMemo } from 'react'
import { useMemoizedFn } from 'ahooks'
import { message as $message } from 'antd'
import findLastIndex from 'lodash/findLastIndex'
import { grantAuthV2UsingPOST, resendSmsAuthCodeUsingPOST } from '@/services/user'
import { wrapResponseData } from '@/utils/wrapRequest'
const getSensitiveOperationErrorCode = code => {
    return code?.split?.('-')?.[1]
}

export default ({ dataSource, length = 4, onOk }) => {
    const [countdown, setCountdown] = useState(0)
    const [sendCount, setSendCount] = useState(0)
    const [confirmLoading, setConfirmLoading] = useState(false)
    const [codes, setCodes] = useState(() => Array(length).fill(''))
    const timerRef = useRef(null)
    const inputRef = useRef(null)
    const [isFocused, setIsFocused] = useState(false)
    const lockSubmitRef = useRef(false)

    const sensitiveOperationErrorCode = getSensitiveOperationErrorCode(dataSource.code)

    const init = useMemoizedFn(() => {
        startCountdown()
        focusInput()
    })

    const focusInput = useMemoizedFn(() => {
        inputRef.current && inputRef.current.focus()
    })

    const sendCode = useMemoizedFn(async () => {
        await wrapResponseData(
            () =>
                resendSmsAuthCodeUsingPOST({
                    sensitiveOperationErrorCode
                }),
            {
                rejection: true
            }
        )

        startCountdown()
    })

    const startCountdown = useMemoizedFn(() => {
        setCountdown(60)
        setSendCount(prev => prev + 1)

        timerRef.current = setInterval(() => {
            setCountdown(prev => {
                const next = prev - 1

                if (next <= 0) {
                    clearInterval(timerRef.current)
                }

                return next
            })
        }, 1000)
    })

    // 处理退格
    const handleBackspace = useMemoizedFn(() => {
        setCodes(prev => {
            const index = findLastIndex(prev, item => item !== '')
            if (index >= 0) {
                const newCode = [...prev]
                newCode[index] = ''
                return newCode
            }
            return prev
        })
    })

    const keyupHandler = useMemoizedFn(e => {
        console.info('keyupHandler:', e)

        // Backspace 键处理
        if (e.code === 'Backspace' || e.key === 'Backspace' || e.keyCode === 8) {
            handleBackspace()
            return
        }
    })

    // 输入变化处理
    const inputChangeHandler = useMemoizedFn(e => {
        const value = e.target.value
        if (!value) return

        // 获取新增的字符
        const newChars = value.replace(/\D/g, '') // 只保留数字

        setCodes(prev => {
            let newCodes = [...prev]
            let updated = false

            // 遍历新增的每个数字字符
            for (const char of newChars) {
                // 找到第一个空位置
                const emptyIndex = newCodes.findIndex(item => item === '')

                // 如果有空位置，则填入数字
                if (emptyIndex !== -1) {
                    newCodes[emptyIndex] = char
                    updated = true
                } else {
                    // 如果没有空位置则停止处理
                    break
                }
            }

            if (updated && newCodes.filter(Boolean).length === length) {
                setTimeout(() => {
                    submit()
                }, 100)
            }

            return updated ? newCodes : prev
        })

        if (inputRef.current) {
            inputRef.current.value = ''
        }
    })

    useEffect(() => {
        return () => {
            timerRef.current && clearTimeout(timerRef.current)
        }
    }, [])

    const checkCodesValid = useMemoizedFn(codes => {
        return codes.length === length && codes.every(item => /^[0-9]$/.test(item))
    })

    const submit = useMemoizedFn(async () => {
        const hasValidCode = checkCodesValid(codes)

        if (!hasValidCode) {
            const msg = `请输入${length}位数字验证码`
            $message.error(msg)
            focusInput()
            return Promise.reject(msg)
        }

        if (lockSubmitRef.current) {
            return
        }

        lockSubmitRef.current = true
        setConfirmLoading(true)

        const { success, message, data } = await grantAuthV2UsingPOST({
            code: codes.join(''),
            sensitiveOperationErrorCode
        }).finally(() => {
            setConfirmLoading(false)
            lockSubmitRef.current = false
        })

        if (!success || !data) {
            const msg = message || '验证失败'
            $message.warn(msg)
            focusInput()
            return Promise.reject(msg)
        }

        return onOk()
    })

    const activeCodeIndex = useMemo(() => {
        if (!isFocused) {
            return -1
        }

        return codes.findIndex(item => !item)
    }, [codes, isFocused])

    const isCodesValid = useMemo(() => {
        return codes.filter(Boolean).length >= length
    }, [codes, length])

    return {
        init,
        dataSource,
        sendCode,
        countdown,
        sendCount,
        activeCodeIndex,
        isCodesValid,
        codes,
        submit,
        inputRef,
        isFocused,
        setIsFocused,
        keyupHandler,
        inputChangeHandler,
        confirmLoading,
        setConfirmLoading
    }
}
